import PropTypes from 'prop-types';
import {useEffect} from 'react';
import {useTheme} from 'react-jss';
import {connect} from 'react-redux';
import MainContentWrapper from '../../components/Accessibility/MainContentWrapper/MainContentWrapper';
import CollapsiblePanel from '../../UI/CollapsiblePanel/CollapsiblePanel';
import isUsingArtemisStyles from '../../v2/selectors/isUsingArtemisStyles';
import getIsRoktEnabledForCompletePage from '../../v2/selectors/RoktSelectors/getIsRoktEnabledForCompletePage';
import getPrivacyConsentFromPrivacyGroup from '../../v2/selectors/RoktSelectors/getPrivacyConsentFromPrivacyGroup';
import AdvertisementLazyLoader from './Advertisment/AdvertisementLazyLoader';
import useStyles from './CompletePage.styles';
import Surface from '../../UI/Surface/Surface';
import setBackgroundImage from '../../v2/actions/setBackgroundImage';
import Translation from '../../v2/lang/Translation';
import {isMobile} from '../../v2/selectors/DeviceSelectors';
import getIsRoktEnabled from '../../v2/selectors/RoktSelectors/getIsRoktEnabled';
import {
  getLocaleFromSiteDataV2,
  getOrderNumber,
  getSettings,
  getSiteDataV2,
  getStart,
  hasEarntLoyaltyPoints,
  isAccountHolder,
  isGuest,
  isNewGuestUpdatedPage,
} from '../../v2/selectors/selectors';
import doMetric from '../../v2/tracking/bigQueryMetricRecorder';
import getUsedPaymentType from '../../v2/tracking/getUsedPaymentType';
import setBackToHomePage from '../../v2/utils/setBackToHomePage';
import Track from '../../v2/utils/tracking';
import getWebloyaltyVersion from '../../v2/webloyalty/getWebLoyaltyVersion';
import Banner from './Banner/Banner';
import Clarus from './Clarus/Clarus';
import './CompletePage.scss';
import CompletePageHeader from './CompletePageHeader/CompletePageHeader';
import CompletePageInformation from './CompletePageInformation/CompletePageInformation';
import CompletePageNextSteps from './CompletePageNextSteps/CompletePageNextSteps';
import CreateAccount from './CreateAccount/CreateAccount';
import EGifting from './EGifting/EGifting';
import GoogleDoubleClick from './GoogleDoubleClick/GoogleDoubleClick';
import MoreTrees from './MoreTrees/MoreTrees';
import OrderNumber from './OrderNumber/OrderNumber';
import PaymentInformation from './PaymentInformation/PaymentInformation';
import Rokt from './Rokt/Rokt';
import RoktCompletePage from './RoktCompletePage';
import ShoeSizeMe from './ShoeSizeMe/ShoeSizeMe';
import SocialSharing from './SocialSharing/SocialSharing';
import useQubit from './Uv/useUv';
import WebLoyaltyArbiter from './WebLoyalty/WebLoyaltyArbiter';

export function CompletePage(props) {
  const theme = useTheme();
  const classes = useStyles({...props, theme});
  
  useQubit({
    qubitApiIntegrationEnabled: props.qubitApiIntegrationEnabled,
    qubitApiIntegrationLoggingEnabled: props.qubitApiIntegrationLoggingEnabled,
    orderNumber: props.orderNumber,
    order: props.order,
    orderTotal: props.orderTotal,
    locale: props.locale,
  });
  
  useEffect(() => {
    setBackToHomePage();
    
    if (props.isNewGuestUpdatedPage) {
      props.setBackgroundImage(props.siteCode);
    }
  
    try {
      doMetric('CompletePage', [
        {name: 'usedPaymentMethod', value: getUsedPaymentType()},
      ], [], 'impression');
    } catch (error) {
      Track.track(error);
    }
  }, []);
  
  const getOrderNumberLine = () => (props.useNewOrderNumberKey ? (
    <OrderNumber orderNumber={props.orderNumber} siteCode={props.siteCode} />
  ) : (
    <p
      className={`CompletePage__orderID ${classes.orderId}`}
      dangerouslySetInnerHTML={{
        __html: Translation.getText('checkout.complete.intro.order.number.text')
          .replace('{0}', props.orderNumber),
      }}
    />
  ));
  
  if (props.caasCompletePageEnabled) {
    return (
      <div className={classes.caasCompletePage}>
        <CompletePageHeader />
        <div>
          <p
            className={`CompletePage__orderID ${classes.caasOrderId}`}
            dangerouslySetInnerHTML={{
              __html: Translation.getText('checkout.complete.intro.order.number.text')
                .replace('{0}', props.orderNumber),
            }}
          />
        </div>
        <Surface>
          <CompletePageInformation />
        </Surface>
      </div>
    );
  }
  
  const renderBeautyHeader = (renderComponent) => {
    if (renderComponent && props.beautyHeader) {
      return (
        <p className={`CompletePage__beautyHeader ${classes.beautyHeader}`}>
          <Translation>beauty.want.more</Translation>
        </p>
      );
    }
  };
  const renderNextStepComponents = () => {
    if (props.showCreateAccount && props.showEGifting && !props.isMobile) {
      return (
        <>
          <div className={`CompletePage__wrapper ${classes.wrapper}`}>
            <CompletePageNextSteps />
            <EGifting />
          </div>
          <div className={`CompletePage__wrapper ${classes.wrapper}`}>
            <CreateAccount />
          </div>
        </>
      );
    }
    if (!props.isMobile) {
      return (
        <>
          <div className={`CompletePage__wrapper ${classes.wrapper}`}>
            <CompletePageNextSteps />
          </div>
          {props.showEGifting && (
            <div className={`CompletePage__wrapper ${classes.wrapper}`}>
              <EGifting />
            </div>
          )}
          {props.showCreateAccount && (
            <div className={`CompletePage__wrapper ${classes.wrapper}`}>
              <CreateAccount />
            </div>
          )}
        </>
      );
      // Mobile rendering
    }
    return (
      <div className={`CompletePage__wrapper ${classes.wrapper}`}>
        <CompletePageNextSteps />
        {props.showEGifting && <EGifting />}
        {props.showCreateAccount && <CreateAccount />}
      </div>
    );
  };
  
  return (
    <MainContentWrapper>
      <div
        data-test="CompletePage"
        className={classes.completePage}
      >
        {props.roktCompleteEnabled ? (
          <RoktCompletePage />
        ) : (
          props.roktEnabled && (
            <Rokt
              orderNumber={props.orderNumber}
              countryCode={props.countryCode}
              locale={props.locale}
              roktId={props.roktId}
              encryptedValues={props.encryptedValues}
              consentedToTargetingPrivacyConsentGroups={props.consentedToTargetingPrivacyConsentGroups}
            />
          )
        )}
        {props.shoeSizeScriptEnabled && (
          <ShoeSizeMe
            orderNumber={props.orderNumber}
            shoeSizeId={props.shoeSizeId}
          />
        )}
        <CompletePageHeader />
        <PaymentInformation />
        {props.isNewGuestUpdatedPage && getOrderNumberLine()}
        <div className={classes.orderCompleteSection} data-test="CompletePage__orderCompleteSection">
          <Clarus guestBanner={props.isNewGuestUpdatedPage} />
          {props.isNewGuestUpdatedPage ? (
            <>
              {renderBeautyHeader(!props.isMobile)}
              <Banner componentName="CompletePage">
                {renderBeautyHeader(props.isMobile)}
                {props.showCreateAccount && <CreateAccount />}
              </Banner>
              {(props.shouldShowWebLoyalty || props.moreTreesUrl || props.advertisementLayout) && (
                <div className={`CompletePage__isNewGuestUpdatedPage-wrapper ${classes.isNewGuestUpdatedPageWrapper}`}>
                  {props.shouldShowWebLoyalty && (
                    <WebLoyaltyArbiter
                      isNewGuestUpdatedPage={props.isNewGuestUpdatedPage}
                      isMobile={props.isMobile}
                      email={props.email}
                      webLoyaltyVersion={props.webLoyaltyVersion}
                    />
                  )}
                  {(props.advertisementLayout) && (
                    <AdvertisementLazyLoader />
                  )}
                  {props.moreTreesUrl && !(props.advertisementLayout) && (
                    <MoreTrees url={props.moreTreesUrl} />
                  )}
                </div>
              )}
            </>
          ) : (
            <Surface>
              <div className={`CompletePage__nextStepComponents ${classes.nextStepComponents}`}>
                {renderNextStepComponents()}
                {props.showSocialSharing && (
                  <div className={`CompletePage__wrapper ${classes.wrapper}`}>
                    <SocialSharing />
                  </div>
                )}
                {(props.shouldShowWebLoyalty || props.moreTreesUrl || (props.isMobile && props.advertisementLayout)) && (
                  <div className={`CompletePage__wrapper ${classes.wrapper}`}>
                    {props.shouldShowWebLoyalty && (
                      <WebLoyaltyArbiter
                        isNewGuestUpdatedPage={props.isNewGuestUpdatedPage}
                        isMobile={props.isMobile}
                        email={props.email}
                        webLoyaltyVersion={props.webLoyaltyVersion}
                      />
                    )}
                    {(props.isMobile && props.advertisementLayout) && (
                      <AdvertisementLazyLoader />
                    )}
                    {props.moreTreesUrl && (
                      <MoreTrees url={props.moreTreesUrl} />
                    )}
                  </div>
                )}
              </div>
            </Surface>
          )}
          <GoogleDoubleClick />
        </div>
        <Surface>
          {props.isNewGuestUpdatedPage ? (
            <CollapsiblePanel label="what.happens.next" componentId="CompletePageInfo" iconEnabled>
              <CompletePageInformation />
            </CollapsiblePanel>
          ) : <CompletePageInformation />}
        </Surface>
        <GoogleDoubleClick block />
      </div>
    </MainContentWrapper>
  );
}

const mapStateToProps = (state) => {
  const settings = getSettings(state);
  const start = getStart(state);
  const isMobileDevice = isMobile(state);
  const guestOrder = isGuest(state);
  const loyaltyEarnt = hasEarntLoyaltyPoints(state);
  const accountHolder = isAccountHolder(state);
  const {siteCode} = getSiteDataV2(state);
  const countryCode = start.order?.billingAddress?.countryCode || start.customer?.location;
  const rokt = settings.rokt_placement;
  const shoeSize = settings.enable_shoe_size_script;
  const webLoyaltyVersion = getWebloyaltyVersion(state);
  const encryptedValues = start?.encryptedValues;
  const newGuestUpdatedPage = isNewGuestUpdatedPage(state);
  
  return {
    showSocialSharing: (!!settings.enable_referrals_social_sharing && !guestOrder)
      || (!!settings.enable_guest_referrals_social_sharing && accountHolder),
    showCreateAccount: guestOrder && !accountHolder,
    showLoyaltyPoints: !guestOrder && loyaltyEarnt,
    isNewGuestUpdatedPage: newGuestUpdatedPage,
    isMobile: isMobileDevice,
    orderNumber: getOrderNumber(state),
    showEGifting: settings.egifting_url,
    shouldShowWebLoyalty: !!(webLoyaltyVersion),
    webLoyaltyVersion,
    moreTreesUrl: settings.more_trees_url,
    isUsingArtemisStyles: isUsingArtemisStyles(state),
    shoeSizeScriptEnabled: shoeSize?.enabled,
    shoeSizeId: shoeSize?.id,
    useNewOrderNumberKey: !!settings?.use_new_order_number_property,
    siteCode,
    roktEnabled: (getIsRoktEnabled(state) && encryptedValues),
    roktCompleteEnabled: getIsRoktEnabledForCompletePage(state),
    roktId: rokt?.id || 'invalid_id',
    email: start?.customer?.email,
    countryCode,
    locale: getLocaleFromSiteDataV2(state)?.replace('_', '-').toLowerCase(),
    name: start?.customer?.fullName || state.createAccount?.fullName || '',
    encryptedValues,
    qubitApiIntegrationEnabled: !!settings?.qubit_api_integration_enabled,
    qubitApiIntegrationLoggingEnabled: !!settings?.qubit_api_integration_logging_enabled,
    order: start?.order,
    orderTotal: start?.orderTotal,
    advertisementLayout: settings?.checkout_complete_ad,
    isGuest: guestOrder,
    consentedToTargetingPrivacyConsentGroups: !!getPrivacyConsentFromPrivacyGroup(state),
    caasCompletePageEnabled: settings?.caas_complete_page_enabled,
  };
};

CompletePage.propTypes = {
  caasCompletePageEnabled: PropTypes.bool,
  isUsingArtemisStyles: PropTypes.bool,
  showSocialSharing: PropTypes.bool,
  showCreateAccount: PropTypes.bool,
  showLoyaltyPoints: PropTypes.bool,
  shoeSizeScriptEnabled: PropTypes.bool,
  roktEnabled: PropTypes.bool,
  roktCompleteEnabled: PropTypes.bool,
  roktId: PropTypes.string,
  shoeSizeId: PropTypes.string,
  useNewOrderNumberKey: PropTypes.bool,
  shouldShowWebLoyalty: PropTypes.bool,
  orderNumber: PropTypes.string.isRequired,
  isNewGuestUpdatedPage: PropTypes.bool.isRequired,
  beautyHeader: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  showEGifting: PropTypes.string,
  moreTreesUrl: PropTypes.string,
  email: PropTypes.string,
  countryCode: PropTypes.string,
  locale: PropTypes.string,
  siteCode: PropTypes.string,
  setBackgroundImage: PropTypes.func,
  name: PropTypes.string,
  webLoyaltyVersion: PropTypes.string,
  encryptedValues: PropTypes.shape({
    firstNameEnc: PropTypes.string,
    lastNameEnc: PropTypes.string,
    emailEnc: PropTypes.string,
  }),
  qubitApiIntegrationEnabled: PropTypes.bool.isRequired,
  qubitApiIntegrationLoggingEnabled: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  order: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  orderTotal: PropTypes.object,
  advertisementLayout: PropTypes.bool,
  isGuest: PropTypes.bool,
};

export default connect(
  mapStateToProps,
  {setBackgroundImage},
)(CompletePage);
